import React from 'react';
import { ClickAwayListener, createStyles, makeStyles, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { faChevronDown, faChevronUp, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

interface MembershipItemSelectorProps<T> {
    label: string;
    options: Array<{
        label: string;
        price: string;
        value: T;
        disabled?: boolean;
    }>;
    onChange: (value: T) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',

            [theme.breakpoints.down('sm')]: {
                gridColumn: 'span 3'
            }
        },
        select: {
            'height': 44,
            'width': '100%',
            'borderRadius': 30,
            'cursor': 'pointer',
            'fontFamily': 'Poppins',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'position': 'relative',
            'padding': '0px 18px 0px 24px',
            'border': '1px solid #D3D3D3',
            'backgroundColor': '#ffffff',
            'zIndex': 20000,

            '& label': {
                fontSize: 14,
                fontWeight: 400,
                pointerEvents: 'none'
            },
            '& svg': {
                fontSize: 16,
                color: '#7A7A7A',
                pointerEvents: 'none'
            }
        },
        open: {
            'borderColor': '#000',
            'borderRadius': '20px 20px 0px 0px',

            '& svg': {
                color: '#000000'
            }
        },
        dropdown: {
            position: 'absolute',
            backgroundColor: '#ffffff',
            padding: '14px 4px 14px 0px',
            border: 'solid 1px #000000',
            borderTop: 'none',
            borderRadius: '0px 0px 20px 20px',
            width: '100%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 20001
        },
        dropdownScroller: {
            'width': '100%',
            'maxHeight': 150,
            'overflowY': 'auto',

            '&::-webkit-scrollbar': {
                width: 5,
                height: 8
            },
            '&::-webkit-scrollbar-track': {
                borderRadius: 5,
                background: '#EAEAEA'
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: 5,
                background: '#C9C9C9'
            },
            '&::-webkit-scrollbar-thumb:hover': {
                background: '#b0b0b0'
            }
        },
        dropdownItem: {
            'height': 'auto',
            'minHeight': 33,
            'fontSize': 16,
            'lineHeight': 1,
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'space-between',
            'flexDirection': 'row',
            'padding': '0px 20px',
            'cursor': 'pointer',

            '&:hover': {
                backgroundColor: '#F8F8F8'
            },

            '& p:last-child': {
                fontSize: 14,
                fontWeight: 500
            }
        }
    })
);

export const MembershipItemSelector = <T,>(props: MembershipItemSelectorProps<T>) => {
    const classes = useStyles();

    const selectorRef = React.useRef<HTMLDivElement>(null);

    const [isOpen, setIsOpen] = React.useState(false);
    const [dropdownPosition, setDropdownPosition] = React.useState({
        top: 0,
        left: 0,
        width: 0
    });

    const isMobile = useMediaQuery('(max-width: 600px)');

    React.useEffect(() => {
        if (!isMobile) {
            document.body.style.overflow = isOpen ? 'hidden' : 'auto';
        }

        if (selectorRef.current) {
            const { top, left, width } = selectorRef.current.getBoundingClientRect();

            setDropdownPosition({
                top: top + selectorRef.current.scrollHeight,
                left: left,
                width: width
            });
        }
    }, [isOpen]);
    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div
                ref={selectorRef}
                className={classes.root}
            >
                <div
                    className={clsx(classes.select, {
                        [classes.open]: isOpen
                    })}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <label>{props.label}</label>

                    <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                </div>

                {isOpen && (
                    <div className={classes.dropdown}>
                        <div className={classes.dropdownScroller}>
                            {props.options.map(
                                (option, i) =>
                                    !option.disabled && (
                                        <div
                                            key={i}
                                            className={classes.dropdownItem}
                                            onClick={() => props.onChange(option.value)}
                                        >
                                            <Typography>{option.label}</Typography>
                                            <Typography>{option.price}</Typography>
                                        </div>
                                    )
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};
